import { queryOptions, useQuery } from "@tanstack/react-query";
import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { API_ROUTES } from "utils/api-route.constants";
import { CardSets, type GameId, ShortSet } from "modules/common/model/models";
import { SETS_COMPACT_QUERY_KEY } from "modules/common/query";
import { STATIC_DATA_CONFIG } from "modules/common/react-query-configs";

/**
 * Creates query options for fetching MTG sets
 * Using queryOptions provides better type safety and reusability
 */
export function createSetsQueryOptions<T = CardSets>(
  fetchFn: () => Promise<CardSets>,
  selectFn?: (data: CardSets) => T
) {
  return queryOptions({
    queryKey: SETS_COMPACT_QUERY_KEY,
    queryFn: fetchFn,
    select: selectFn,
    ...STATIC_DATA_CONFIG, // 30min stale, no refetch - perfect for set reference data
  });
}

export const useSets = () => {
  const fetch = useFetchWrapper();

  const fetchSets = () => {
    return fetch.publicGet(API_ROUTES.SETS.GET_ALL, null);
  };

  const query = useQuery(createSetsQueryOptions(fetchSets));
  const getSetByCode = (setCode: string, game: GameId) =>
    query.data?.[game]?.[setCode];

  return {
    query: query,
    sortedSets: (): ShortSet[] => {
      if (query.data && query.isSuccess) {
        const res = Object.values(query.data)
          .flatMap((gameSets) => Object.values(gameSets ?? {}))
          // A stale cached v1 payload is keyed by set code instead of game, so
          // the values here are strings rather than sets — drop them instead of
          // handing undefined names to every consumer.
          .filter((set) => set?.name != null && set?.code != null)
          .map((set) => ({
            name: set.name,
            code: set.code,
            releaseDate: set.releaseDate,
            keyruneCode: set.keyruneCode,
            game: set.game,
          }));
        res.sort((a, b) => (a.name > b.name ? 1 : -1));
        return res;
      }
      return [];
    },

    // Utility functions for cleaner component code
    getSetByCode,

    getKeyruneCode: (setCode: string, game: GameId) => {
      return getSetByCode(setCode, game)?.keyruneCode || setCode;
    },

    getSetName: (setCode: string, game: GameId) => {
      return getSetByCode(setCode, game)?.name || setCode;
    },

    getKeyruneClass: (setCode: string, game: GameId, size?: "2x") => {
      const set = getSetByCode(setCode, game);
      const keyrune =
        set?.keyruneCode !== "DEFAULT"
          ? set?.keyruneCode?.toLowerCase()
          : setCode.toLowerCase() || "DEFAULT";

      const sizeClass = size === "2x" ? "ss-2x" : "";
      return `ss ss-${keyrune} text-center w-fit ${sizeClass}`.trim();
    },
  };
};
